import "@/i18n/config";
import React, { useState } from "react";
import { toast } from "react-hot-toast";
import SVG from "react-inlinesvg";
import { useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";

import LeftURL from "@assets/icons/left.svg";

import { Pagination } from "@/client_app/components/Pagination";
import { ApplicationsItem } from "@/organization_app/features/jobs/components/ApplicationsItem";
import {
  getJobApplications,
  sendMessage,
} from "@/organization_app/features/jobs/services/FetchAPIService";
import { PrimaryButton } from "@/ui/buttons";
import { Checkbox } from "@/ui/Checkbox";
import { FullLayoutModal } from "@/ui/FullLayoutModal";

const limit = 10;
const defaultValue =
  "Hey %{first_name},\n\n" +
  "#TalentName# from Weploy here 👋\n\n" +
  "We've had a new job opportunity become available. We think you could be perfect for the role! 🤩\n\n" +
  "Company: <<COMPANY NAME>>\n" +
  "Job: <<ROLE TITLE>>\n" +
  "Duration: 3 months with a view of going perm\n" +
  "Hours: 9.00am - 5.00pm, Mon - Fri\n" +
  "Payrate: $29.86 OR $32.69 p/h plus super\n" +
  "Location: SUBURB (full time onsite or part time home / office or WFH)\n" +
  "<<OPTIONAL: Visa Status: Only those with longer term visa's (2+ years) are accepted at this time>>\n\n" +
  "Please reply YES to confirm your interest and you will hear from us today.\n\n" +
  "-The Weploy Team";
export function ApplicationsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [paginationOffset, setPaginationOffset] = useState(0);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [smsValue, setSmsValue] = useState(defaultValue);

  const queryParams = {
    limit: limit,
    offset: paginationOffset,
    jobId,
  };

  const { data, isFetching, isSuccess, error } = useQuery(
    ["jobApplications", queryParams],
    () =>
      getJobApplications(queryParams).then((response) => {
        return response.json();
      })
  );

  const send = () => {
    sendMessage({
      message: smsValue,
      application_ids: selectedApplicationIds,
    }).then();
    setIsOpen(false);
    toast.success("Message was send successful");
  };

  const applications = data?.applications || [];
  const totalCount = data?.total_count || 0;
  const jobTitle = data?.job_title || "";

  const applicationIds = applications.map((application) => application.id);

  const isSelectAll =
    selectedApplicationIds.length !== 0 &&
    selectedApplicationIds.length === applicationIds.length;
  function handleSelectApplication(applicationId): void {
    setSelectedApplicationIds((selectedAttendanceIds): string[] => {
      if (selectedAttendanceIds.includes(applicationId)) {
        return selectedAttendanceIds.filter((att) => att !== applicationId);
      } else {
        return [...selectedAttendanceIds, applicationId];
      }
    });
  }

  function handleSelectAllApplications(): void {
    setSelectedApplicationIds((selectedApplicationIds): string[] => {
      return selectedApplicationIds.length === applicationIds.length
        ? []
        : applicationIds;
    });
  }

  if (error) {
    return <div>Error!</div>;
  }
  return (
    <div className='text-legacyColor-neutral-1-500'>
      <header className='px-12 py-8 bg-legacyColor-neutral-2-100'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center'>
            <SVG
              src={LeftURL}
              className='icon-lg fill-current mr-4 cursor-pointer'
              onClick={() =>
                navigate({
                  pathname: "/admin/jobs",
                })
              }
            />
            <h1 className='text-headline2 mb-4'>Job Applications</h1>
          </div>

          <h5>{jobTitle}</h5>
        </div>
      </header>
      <section className='m-12'>
        {isFetching && <div>Loading...</div>}

        {!isFetching && isSuccess && (
          <div>
            {applications.length === 0 && "No data"}
            {applications.length > 0 && (
              <>
                <div className='flex items-center'>
                  <p className='m-2 font-bold text-caption'>
                    <span>{selectedApplicationIds.length}</span> selected
                  </p>
                  <PrimaryButton
                    label='Bulk SMS'
                    disabled={selectedApplicationIds.length === 0}
                    onClick={() => setIsOpen(true)}
                  />
                </div>
                <div className='pl-2 py-4 flex items-center'>
                  <Checkbox
                    onChange={handleSelectAllApplications}
                    checked={isSelectAll}
                  />
                  <div className='pl-2'>
                    {isSelectAll ? "Unselect all" : "Select all"}
                  </div>
                </div>

                <table className='border border-gray-300 mb-4'>
                  <colgroup>
                    <col style={{ width: "2%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "25%" }} />
                    <col style={{ width: "27%" }} />
                    <col style={{ width: "30%" }} />
                  </colgroup>
                  <tbody>
                    {applications.map((application, index) => (
                      <ApplicationsItem
                        application={application}
                        selected={selectedApplicationIds.includes(
                          application.id
                        )}
                        onSelect={() => handleSelectApplication(application.id)}
                      />
                    ))}
                  </tbody>
                </table>
                <Pagination
                  totalCount={totalCount}
                  onPageChange={(data) => setPaginationOffset(data)}
                  perPage={limit}
                />
              </>
            )}
          </div>
        )}

        {isOpen && (
          <FullLayoutModal
            isOpen={isOpen}
            closeModal={() => setIsOpen(false)}
            title={"SEND MESSAGES"}
            subTitle={""}
          >
            <div className='w-3/4'>
              <div className='py-2'>
                To {selectedApplicationIds.length} recipients
              </div>
              <div>
                <textarea
                  className='form-control min-h-[25rem]'
                  value={smsValue}
                  onChange={(e) => setSmsValue(e.target.value)}
                />
              </div>
              <div className='pt-2 justify-self-end'>
                <PrimaryButton label={"Send"} onClick={send} />
              </div>
            </div>
          </FullLayoutModal>
        )}
      </section>
    </div>
  );
}
